import JSDB from '../../fromKotlin/nk';
import IItem from '../../types/product_delivery';
import { dateObjectToString } from '../../fromKotlin/nkutils';
import { fetchTaklitProducts, TaklitProduct } from './taklit.api';

/** Same gate as Android TableNoteManagerNew: user.taklit >= 1. */
export function isTaklitEnabled(): boolean {
    return (JSDB().getUser().first?.taklit ?? 0) >= 1;
}

/** Thu+ → Wednesday of that week; else yesterday — mirrors NKDBInterface.getTaklitProductPrev. */
function prevMarketDay(date: string): string {
    const dm = JSDB().getDatesManipulator();
    const day = dm.getDayOfWeek(date);
    return day >= 4 ? dm.getNextDay(date, 3 - day) : dm.getNextDay(date, -1);
}

function toDateStr(date: string | Date): string {
    return typeof date === 'string' ? date : dateObjectToString(date);
}

/**
 * Loads taklit rows for the note date (or prev market day when taklit >= 2)
 * and returns a map keyed by taklit catalog id.
 */
export async function loadTaklitLookup(date: string | Date): Promise<Map<number, TaklitProduct>> {
    const user = JSDB().getUser().first;
    const map = new Map<number, TaklitProduct>();
    if (!user || user.taklit < 1) return map;

    const dateStr = toDateStr(date);
    if (user.taklit >= 2) {
        const list = await fetchTaklitProducts(prevMarketDay(dateStr));
        for (const t of list) map.set(t.id, t);
        return map;
    }

    const list = await fetchTaklitProducts(dateStr);
    const prevDay = prevMarketDay(dateStr);
    for (const t of list) {
        if (t.date === dateStr || t.date === prevDay) map.set(t.id, t);
    }
    return map;
}

export function getProductTaklitLink(
    productId: number,
    nkObject?: any,
): { taklit_id: number; taklit_type: number } | null {
    const p = nkObject?.product ?? JSDB().getClientProduct(productId)?.first;
    if (!p) return null;
    const taklit_id = Number(p.taklit_id ?? -1);
    const taklit_type = Number(p.taklit_type ?? -1);
    if (Number.isNaN(taklit_id) || taklit_id < 0) return null;
    return { taklit_id, taklit_type };
}

/** Unit price from taklit (pricea / priceb). Discount is left on the client menu. */
export function unitPriceFromTaklit(
    lookup: Map<number, TaklitProduct>,
    taklit_id: number,
    taklit_type: number,
): number | null {
    const tp = lookup.get(taklit_id);
    if (!tp) return null;
    if (taklit_type === 1 && tp.priceb > 0) return tp.priceb;
    return tp.pricea > 0 ? tp.pricea : null;
}

export type TaklitApplyOpts = { markTouched?: boolean; onlyUntouched?: boolean };

/** Overlay one line from an already-loaded taklit map (Android prepareProducts). */
export function applyTaklitPriceFromLookup(
    item: IItem,
    lookup: Map<number, TaklitProduct>,
    opts?: TaklitApplyOpts,
): { item: IItem; applied: boolean } {
    if (lookup.size === 0) return { item, applied: false };
    if (opts?.onlyUntouched && item.priceTouched) return { item, applied: false };
    const link = getProductTaklitLink(item.id, item.nkObject);
    if (!link) return { item, applied: false };
    const price = unitPriceFromTaklit(lookup, link.taklit_id, link.taklit_type);
    if (price == null) return { item, applied: false };
    return {
        item: {
            ...item,
            totalPrice: price,
            ...(opts?.markTouched ? { priceTouched: true } : {}),
        },
        applied: true,
    };
}

export async function applyTaklitPricesToItems(
    items: IItem[],
    date: string | Date,
    opts?: TaklitApplyOpts,
): Promise<{ items: IItem[]; applied: number }> {
    if (!isTaklitEnabled() || items.length === 0) {
        return { items, applied: 0 };
    }
    const lookup = await loadTaklitLookup(date);
    if (lookup.size === 0) return { items, applied: 0 };

    let applied = 0;
    const next = items.map((item) => {
        const result = applyTaklitPriceFromLookup(item, lookup, opts);
        if (result.applied) applied += 1;
        return result.item;
    });
    return { items: next, applied };
}
